import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import {
  QualificationDraft,
  QualifyResult,
  buildQualifyPayload,
  buildQualifyUrl
} from './qualification';

/** Bilan d'un appel abouti : une demande envoyée tombe dans l'un de ces trois cas. */
export type QualifyReport = {
  /** Demandes passées à QUALIFIED. */
  qualifiedCount: number;
  /** Demandes sauvegardées, mais qu'il reste à compléter. */
  incompleteCount: number;
  /** Demandes refusées par l'API. */
  failedCount: number;
  /** Refus de l'API, un par demande, dans l'ordre reçu ; vide s'il n'y en a pas. */
  errors: QualifyError[];
};

/** Refus d'une demande, tel que l'API le formule : le message nomme déjà l'équipement. */
export type QualifyError = {
  /** Clé de l'entrée dans `errors` de l'API : l'id de la demande refusée. */
  requestId: string;
  message: string;
};

export type QualifyState = {
  /**
   * `onSuccess` reçoit le résultat brut de l'API : le panneau s'en sert pour publier
   * son bilan et décider de la bascule, la modale ne s'ouvrant pas sur un succès sans
   * réserve.
   */
  qualify: (
    draft: QualificationDraft,
    options?: { onSuccess?: (result: QualifyResult) => void }
  ) => void;
  isQualifying: boolean;
  hasFailed: boolean;
  /** Bilan du dernier appel abouti, `null` tant qu'il n'y en a pas eu. */
  report: QualifyReport | null;
  dismiss: () => void;
};

/**
 * Qualifie les demandes cochées.
 *
 * Ce POST sauvegarde le formulaire et fait passer à QUALIFIED en un seul appel : il n'y
 * a plus d'`upgrade-status` à enchaîner. Les demandes restées incomplètes, comme celles
 * en échec, gardent leur case cochée pour être complétées ou relancées sans tout
 * re-cocher.
 */
export function useQualifyRequests(): QualifyState {
  const { state, dispatch } = useInterventionCreation();
  const queryClient = useQueryClient();

  const [report, setReport] = useState<QualifyReport | null>(null);

  const mutation = useMutation<QualifyResult, unknown, QualificationDraft>({
    mutationFn: async (draft) => {
      if (state.clientId === null) {
        throw new Error('Aucun client sélectionné');
      }

      const response = await axios.post(
        buildQualifyUrl(state.clientId),
        buildQualifyPayload(draft, state.selectedRequestIds)
      );

      return response.data;
    },
    onSuccess: (result) => {
      // `pds/to-select/` n'est plus lu par cet écran : rien à invalider de ce côté.
      queryClient.invalidateQueries(['exocet-pds-to-qualify']);
      // Une qualification fait passer les demandes à l'étape Affectation : sans cette
      // seconde invalidation, sa liste et son compteur resteraient périmés. Symétrique
      // du renvoi, qui invalide la qualification depuis l'affectation.
      queryClient.invalidateQueries(['exocet-pds-to-simple-assign']);

      // `missing_fields` est indexé par id de demande, en chaîne.
      const incompleteIds = Object.keys(result.missing_fields).map(Number);

      setReport({
        qualifiedCount: result.qualified_ids.length,
        incompleteCount: incompleteIds.length,
        failedCount: result.failed_ids.length,
        errors: (result.errors ?? []).flatMap((error) =>
          Object.entries(error).map(([requestId, message]) => ({ requestId, message }))
        )
      });

      // Une demande refusée et une demande incomplète sont deux cas distincts, mais
      // aucune des deux n'est allée au bout : les deux restent cochées.
      dispatch({
        type: 'setSelectedRequests',
        ids: [...result.failed_ids, ...incompleteIds]
      });
    }
  });

  return {
    qualify: mutation.mutate,
    isQualifying: mutation.isLoading,
    hasFailed: mutation.isError,
    report,
    dismiss: () => {
      setReport(null);
      mutation.reset();
    }
  };
}
